import { Layout as AntLayout, Space, Button, Typography } from 'antd';
import { MenuOutlined, FullscreenOutlined, FullscreenExitOutlined, DesktopOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { StoreSelector } from './StoreSelector';
import { NotificationBell } from './NotificationBell';
import { GoldPricePill } from './GoldPricePill';
import { HeaderUserMenu } from './HeaderUserMenu';
import { usePosFullscreen } from '@/pages/POS/PosFullscreenContext';

const { Header: AntHeader } = AntLayout;

interface HeaderProps {
  isMobile: boolean;
  onToggleSidebar?: () => void;
}

/**
 * App Shell topbar (EG-355 §4). 4 phần:
 *  - Store Selector (dropdown chọn cửa hàng làm việc)
 *  - Notification Bell (badge + popover 5 thông báo gần nhất)
 *  - Gold Price Pill (popover hiển thị toàn bộ giá — useGoldPrice realtime)
 *  - User Avatar (dropdown: Profile / Đổi pw / Đăng xuất)
 *
 * Mobile: hamburger trái mở sidebar drawer; ẩn GoldPricePill cho gọn.
 * POS route: thêm App Fullscreen + Browser Fullscreen buttons.
 */
export const Header = ({ isMobile, onToggleSidebar }: HeaderProps) => {
  const { posAppFullscreen, toggleAppFS, toggleBrowserFS } = usePosFullscreen();
  const location = useLocation();
  const isPOSRoute = location.pathname.startsWith('/pos');

  return (
    <AntHeader
      style={{
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        padding: isMobile ? '0 12px' : '0 16px',
        height: 56,
        lineHeight: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {posAppFullscreen ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <Button
            type="text"
            icon={<FullscreenExitOutlined />}
            onClick={toggleAppFS}
            style={{ color: '#a4193d' }}
            title="Thoát App Fullscreen"
          />
          <Typography.Text strong style={{ color: '#a4193d', flexShrink: 0 }}>POS</Typography.Text>
          {/* Portal target — POSPage renders PosTabsBar vào đây khi posAppFullscreen.
              flex-direction: column + justify-content: flex-end → tabs neo đáy Header
              để border-bottom của bar trùng border-bottom của Header (Chrome-tab style).
              Cross-axis stretch (default) → bar chiếm full chiều ngang slot. */}
          <div
            id="pos-tabs-portal-slot"
            style={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              overflow: 'hidden',
            }}
          />
        </div>
      ) : isMobile && onToggleSidebar ? (
        <Button
          type="text"
          icon={<MenuOutlined style={{ fontSize: 18 }} />}
          onClick={onToggleSidebar}
          aria-label="Mở menu"
        />
      ) : (
        <span />
      )}
      <Space size={isMobile ? 8 : 16} align="center">
        {!isMobile && <GoldPricePill />}
        <StoreSelector />
        <NotificationBell />
        {isPOSRoute && (
          <>
            <Button
              type="text"
              icon={posAppFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleAppFS}
              title={posAppFullscreen ? 'Thoát App Fullscreen' : 'App Fullscreen (ẩn sidebar)'}
            />
            <Button
              type="text"
              icon={<DesktopOutlined />}
              onClick={toggleBrowserFS}
              title="Browser Fullscreen"
            />
          </>
        )}
        <HeaderUserMenu />
      </Space>
    </AntHeader>
  );
};
